import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

// Categorical slots 1 and 2, validated as a pair on the white page surface
// (colour blind ΔE 24.7, >= 3:1 contrast). Not status colours: spending is
// not an error.
const COLORS = {
  earned: '#2a78d6',
  spent: '#eb6834',
  unchanged: '#8b949e'
};

const LABELS = {
  earned: 'Earned',
  spent: 'Spent',
  unchanged: 'No change'
};

const SURFACE = '#ffffff';
const INK = '#24292f';
const MUTED_INK = '#57606a';
const AXIS = '#d0d7de';

const DEFAULT_WIDTH = 960;
const PADDING_X = 28;
const MIN_STEM_GAP = 8;
const BASE_STEM = 26;
const LANE_HEIGHT = 16;
const LABEL_FONT_SIZE = 11;
const LABEL_CHAR_WIDTH = 6.8;
const LABEL_PADDING = 6;
const AXIS_BAND = 30;
const HIT_WIDTH = 24;

const DAY = 24 * 3600 * 1000;
const HOUR = 3600 * 1000;

const kindOf = tokens => {
  if (tokens > 0) return 'earned';
  if (tokens < 0) return 'spent';
  return 'unchanged';
};

const formatTokens = tokens => {
  const amount = Math.abs(tokens).toLocaleString(undefined, {
    maximumFractionDigits: 2
  });
  if (tokens > 0) return `+${amount}`;
  if (tokens < 0) return `−${amount}`;
  return amount;
};

const formatDateTime = time =>
  `${new Date(time).toLocaleDateString()} ${new Date(time).toLocaleTimeString(
    [],
    { hour: '2-digit', minute: '2-digit' }
  )}`;

// Ticks on calendar boundaries, so they read as dates and not as random times
const buildTicks = (minTime, maxTime, maxTicks) => {
  const span = maxTime - minTime;

  if (span <= 2 * DAY) {
    const step =
      [1, 2, 3, 6, 12]
        .map(hours => hours * HOUR)
        .find(candidate => span / candidate <= maxTicks) || 12 * HOUR;
    const first = new Date(minTime);
    first.setMinutes(0, 0, 0);

    const ticks = [];
    for (let t = first.getTime(); t <= maxTime; t += step) {
      if (t >= minTime) {
        ticks.push({
          time: t,
          label: new Date(t).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })
        });
      }
    }
    return ticks;
  }

  const stepDays =
    [1, 2, 7, 14, 30, 60].find(days => span / (days * DAY) <= maxTicks) || 90;
  const first = new Date(minTime);
  first.setHours(0, 0, 0, 0);
  first.setDate(first.getDate() + 1);

  const ticks = [];
  for (
    let day = new Date(first.valueOf());
    day.getTime() <= maxTime;
    day.setDate(day.getDate() + stepDays)
  ) {
    ticks.push({
      time: day.getTime(),
      label: day.toLocaleDateString([], { day: 'numeric', month: 'short' })
    });
  }
  return ticks;
};

const buildLayout = (transactions, width) => {
  const events = (transactions || [])
    .filter(t => t.createdAt && typeof t.tokens === 'number')
    .map(t => ({
      ...t,
      time: new Date(t.createdAt).getTime(),
      kind: kindOf(t.tokens),
      label: formatTokens(t.tokens)
    }))
    .sort((a, b) => a.time - b.time);

  if (!events.length) return null;

  const plotLeft = PADDING_X;
  const plotRight = Math.max(width - PADDING_X, plotLeft + 1);
  const minTime = events[0].time;
  const maxTime = events[events.length - 1].time;
  const span = maxTime - minTime;

  const toX = time =>
    span
      ? plotLeft + ((time - minTime) / span) * (plotRight - plotLeft)
      : (plotLeft + plotRight) / 2;

  // Events at (almost) the same moment are nudged apart just enough that their
  // stems never sit on top of each other, keeping their order
  const gap = Math.min(
    MIN_STEM_GAP,
    (plotRight - plotLeft) / Math.max(events.length - 1, 1)
  );
  const xs = events.map(e => toX(e.time));
  for (let i = 1; i < xs.length; i += 1) {
    xs[i] = Math.max(xs[i], xs[i - 1] + gap);
  }
  xs[xs.length - 1] = Math.min(xs[xs.length - 1], plotRight);
  for (let i = xs.length - 2; i >= 0; i -= 1) {
    xs[i] = Math.min(xs[i], xs[i + 1] - gap);
  }

  // Every amount gets a label, so labels that would overlap move up a lane.
  // The stem grows to reach it: the height places the label, the label
  // carries the amount
  const laneEnds = [];
  const placed = events.map((event, index) => {
    const labelWidth = event.label.length * LABEL_CHAR_WIDTH;
    const labelX = Math.min(
      Math.max(xs[index], labelWidth / 2),
      width - labelWidth / 2
    );
    const start = labelX - labelWidth / 2;
    const end = labelX + labelWidth / 2;

    let lane = laneEnds.findIndex(laneEnd => laneEnd + LABEL_PADDING <= start);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = end;

    return { ...event, x: xs[index], labelX, lane };
  });

  const laneCount = laneEnds.length;
  const axisY = LABEL_FONT_SIZE + 6 + (laneCount - 1) * LANE_HEIGHT + BASE_STEM;
  const maxTicks = Math.max(2, Math.floor((plotRight - plotLeft) / 90));

  return {
    events: placed.map(event => ({
      ...event,
      labelY: axisY - BASE_STEM - event.lane * LANE_HEIGHT
    })),
    axisY,
    height: axisY + AXIS_BAND,
    plotLeft,
    plotRight,
    ticks: span
      ? buildTicks(minTime, maxTime, maxTicks).map(tick => ({
          ...tick,
          x: toX(tick.time)
        }))
      : [
          {
            time: minTime,
            x: toX(minTime),
            label: new Date(minTime).toLocaleDateString()
          }
        ]
  };
};

function TokenTimeline({ transactions }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [activeId, setActiveId] = useState(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const update = () => setWidth(element.clientWidth || DEFAULT_WIDTH);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => buildLayout(transactions, width), [
    transactions,
    width
  ]);

  if (!layout) {
    return <p style={styles.empty}>No transactions to show.</p>;
  }

  const totals = layout.events.reduce(
    (acc, event) => ({
      ...acc,
      [event.kind]: (acc[event.kind] || 0) + event.tokens
    }),
    {}
  );
  const kindsShown = ['earned', 'spent', 'unchanged'].filter(kind =>
    layout.events.some(event => event.kind === kind)
  );

  const active = layout.events.find(event => event.id === activeId);

  return (
    <div>
      <div style={styles.legend}>
        {kindsShown.map(kind => (
          <span key={kind} style={styles.legendItem}>
            <span
              style={{ ...styles.legendSwatch, background: COLORS[kind] }}
            />
            {LABELS[kind]}
            {kind !== 'unchanged' ? (
              <span style={styles.legendTotal}>
                {formatTokens(totals[kind] || 0)}
              </span>
            ) : null}
          </span>
        ))}
      </div>

      <div ref={containerRef} style={styles.chart}>
        <svg
          width={width}
          height={layout.height}
          role="img"
          aria-label={`Tokens earned and spent over time, ${
            layout.events.length
          } transactions`}
          style={styles.svg}
        >
          <line
            x1={layout.plotLeft - 8}
            x2={layout.plotRight + 8}
            y1={layout.axisY}
            y2={layout.axisY}
            stroke={AXIS}
            strokeWidth={1}
          />

          {layout.ticks.map(tick => (
            <g key={`tick-${tick.time}`}>
              <line
                x1={tick.x}
                x2={tick.x}
                y1={layout.axisY}
                y2={layout.axisY + 4}
                stroke={AXIS}
                strokeWidth={1}
              />
              <text
                x={tick.x}
                y={layout.axisY + 18}
                textAnchor="middle"
                fontSize={11}
                fill={MUTED_INK}
                style={styles.tabular}
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* Taller stems first, so shorter stems are drawn on top of them */}
          {[...layout.events]
            .sort((a, b) => b.lane - a.lane)
            .map(event => (
              <line
                key={`stem-${event.id}`}
                x1={event.x}
                x2={event.x}
                y1={layout.axisY}
                y2={event.labelY + 4}
                stroke={COLORS[event.kind]}
                strokeWidth={activeId === event.id ? 3 : 2}
                strokeLinecap="round"
                opacity={activeId && activeId !== event.id ? 0.3 : 1}
              />
            ))}

          {layout.events.map(event => (
            <circle
              key={`dot-${event.id}`}
              cx={event.x}
              cy={layout.axisY}
              r={4}
              fill={COLORS[event.kind]}
              stroke={SURFACE}
              strokeWidth={2}
              opacity={activeId && activeId !== event.id ? 0.3 : 1}
            />
          ))}

          {/* A surface halo masks any stem running behind a label */}
          {layout.events.map(event => (
            <text
              key={`label-${event.id}`}
              x={event.labelX}
              y={event.labelY}
              textAnchor="middle"
              fontSize={LABEL_FONT_SIZE}
              fontWeight={600}
              fill={INK}
              stroke={SURFACE}
              strokeWidth={3}
              paintOrder="stroke"
              opacity={activeId && activeId !== event.id ? 0.3 : 1}
              style={styles.tabular}
            >
              {event.label}
            </text>
          ))}

          {layout.events.map(event => (
            <rect
              key={`hit-${event.id}`}
              x={event.x - HIT_WIDTH / 2}
              y={event.labelY - LABEL_FONT_SIZE - 2}
              width={HIT_WIDTH}
              height={layout.axisY - event.labelY + LABEL_FONT_SIZE + 8}
              fill="transparent"
              tabIndex={0}
              aria-label={`${formatTokens(event.tokens)} tokens, ${event.type ||
                'transaction'}, ${formatDateTime(event.time)}`}
              onMouseEnter={() => setActiveId(event.id)}
              onMouseLeave={() => setActiveId(null)}
              onFocus={() => setActiveId(event.id)}
              onBlur={() => setActiveId(null)}
              style={styles.hit}
            />
          ))}
        </svg>

        {active ? (
          <div
            style={{
              ...styles.tooltip,
              left: Math.min(Math.max(active.x - 90, 0), width - 180),
              top: layout.axisY + AXIS_BAND - 4
            }}
          >
            <div style={styles.tooltipValue}>
              <span
                style={{
                  ...styles.tooltipKey,
                  background: COLORS[active.kind]
                }}
              />
              {formatTokens(active.tokens)} tokens
            </div>
            <div style={styles.tooltipLine}>{active.type || '-'}</div>
            {active.shopItemName ? (
              <div style={styles.tooltipLine}>{active.shopItemName}</div>
            ) : null}
            {active.surveyName ? (
              <div style={styles.tooltipLine}>{active.surveyName}</div>
            ) : null}
            <div style={styles.tooltipMuted}>{formatDateTime(active.time)}</div>
            <div style={styles.tooltipMuted}>
              balance {active.newBalance === null ? '-' : active.newBalance}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = {
  empty: {
    color: '#666'
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '18px',
    margin: '12px 0 6px',
    fontSize: '13px',
    color: MUTED_INK
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  legendSwatch: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block'
  },
  legendTotal: {
    color: INK,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums'
  },
  chart: {
    position: 'relative',
    width: '100%'
  },
  svg: {
    display: 'block',
    overflow: 'visible'
  },
  tabular: {
    fontVariantNumeric: 'tabular-nums'
  },
  hit: {
    cursor: 'pointer',
    outline: 'none'
  },
  tooltip: {
    position: 'absolute',
    width: '180px',
    padding: '8px 10px',
    background: SURFACE,
    border: '1px solid #d0d7de',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    fontSize: '12px',
    pointerEvents: 'none',
    zIndex: 1
  },
  tooltipValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: 600,
    color: INK
  },
  tooltipKey: {
    width: '10px',
    height: '2px',
    borderRadius: '1px',
    display: 'inline-block'
  },
  tooltipLine: {
    color: INK,
    marginTop: '2px'
  },
  tooltipMuted: {
    color: MUTED_INK,
    marginTop: '2px'
  }
};

export default TokenTimeline;
